import { useState } from "react";
import PedirReuniaoView from "./PedirReuniaoView";

/**
 * SOLICITAR ATUALIZAÇÃO — a aba que existe em todos os sistemas.
 *
 * É por aqui que quem usa o sistema pede uma adaptação, uma configuração nova
 * ou avisa de um erro. O pedido cai direto na fila do Desenvolvimento (não vira
 * e-mail nem mensagem solta) e volta com um NÚMERO — é ele que a pessoa cita
 * quando quiser saber o andamento.
 *
 * O formulário passa pelo servidor DESTE sistema, que repassa ao Desenvolvimento
 * (ver server/solicitar-atualizacao.ts): o segredo do canal não pode ficar no
 * navegador. Quem está pedindo vem do login, não de um campo digitável.
 */

const TIPOS = ["Adaptação", "Nova função", "Correção", "Configuração", "Dúvida"];
const PRIORIDADES = ["Baixa", "Normal", "Alta", "Urgente"];

/* O estilo do formulário vem junto, como no PedirReuniaoView: este arquivo é o
   MESMO nos 9 sistemas Bun e só alguns têm .form-card/.solicitar-* no
   styles.css. Fica preso a .solicitar-aba para não mexer em nada fora da aba. */
const CSS = `
.solicitar-aba { display: flex; flex-direction: column; gap: 16px; }
.solicitar-aba .form-card {
  max-width: 980px; background: var(--surface); border: 1px solid var(--border);
  border-left: 4px solid var(--brand); border-radius: 14px; padding: 20px; box-shadow: var(--shadow);
}
.solicitar-aba .section-title { margin: 0 0 6px; font-size: 16px; font-weight: 700; color: var(--text); }
.solicitar-aba .solicitar-form > .muted { margin: 0 0 16px; }
.solicitar-aba .solicitar-foot { display: flex; justify-content: flex-end; margin-top: 16px; }
`;

/* A aba tem duas partes: PEDIR REUNIÃO com o Desenvolvimento (e ver a resposta)
   e, embaixo, o formulário para pedir um ajuste, uma função nova ou avisar de
   um erro — liberado em todos os sistemas em 24/09/2026. */
export default function SolicitarAtualizacaoView({ usuario }: { usuario: string }) {
  return (
    <div className="solicitar-aba">
      <style>{CSS}</style>
      <PedirReuniaoView usuario={usuario} />
      <FormularioSolicitacao usuario={usuario} />
    </div>
  );
}

function FormularioSolicitacao({ usuario }: { usuario: string }) {
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState(TIPOS[0]);
  const [prioridade, setPrioridade] = useState("Normal");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<{ id?: number; mensagem?: string } | null>(null);

  async function enviar() {
    setErro(null);
    if (!titulo.trim()) {
      setErro("Escreva um resumo do que você precisa.");
      return;
    }
    setEnviando(true);
    try {
      const r = await fetch("/api/solicitar-atualizacao", {
        method: "POST",
        headers: { "content-type": "application/json", "x-sercamp-usuario": usuario },
        body: JSON.stringify({ titulo, descricao, tipo, prioridade, email, telefone }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErro(body?.error ?? "Não foi possível enviar a solicitação.");
        setEnviando(false);
        return;
      }
      setEnviado({ id: body.id, mensagem: body.mensagem });
      setTitulo("");
      setDescricao("");
      setTipo(TIPOS[0]);
      setPrioridade("Normal");
    } catch {
      setErro("Não foi possível falar com o servidor. Tente de novo em instantes.");
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="form-card">
        <h3 className="section-title">Solicitação enviada</h3>
        <p>
          {enviado.mensagem ??
            "Sua solicitação foi registrada. O desenvolvedor vai analisar e retornar."}
        </p>
        <p className="muted">
          Guarde o número <strong>#{enviado.id}</strong> — é por ele que você acompanha o
          andamento com o desenvolvedor.
        </p>
        <div className="solicitar-foot">
          <button className="btn-primary btn-sm" onClick={() => setEnviado(null)}>
            Enviar outra solicitação
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-card solicitar-form">
      <h3 className="section-title">Solicitar Atualização</h3>
      <p className="muted">
        Precisa de um ajuste, de um campo novo ou achou um erro? Descreva aqui: o pedido vai
        direto para quem cuida dos sistemas, com o seu nome e a data.
      </p>

      <div className="form-grid">
        <label className="field field-wide">
          <span>
            O que você precisa <span className="req">*</span>
          </span>
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: incluir a coluna de CPF na listagem de colaboradores"
            autoFocus
          />
        </label>
        <label className="field field-wide">
          <span>Explique com detalhes</span>
          <textarea
            rows={5}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Conte onde acontece, o que você tenta fazer hoje e como gostaria que funcionasse."
          />
        </label>
        <label className="field">
          <span>Tipo</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Urgência</span>
          <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
            {PRIORIDADES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Seu e-mail (para retorno)</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
        </label>
        <label className="field">
          <span>Seu WhatsApp</span>
          <input value={telefone} onChange={(e) => setTelefone(e.target.value)} inputMode="tel" />
        </label>
      </div>

      {erro && <div className="form-error">{erro}</div>}

      <div className="solicitar-foot">
        <button className="btn-primary" onClick={enviar} disabled={enviando}>
          {enviando ? "Enviando…" : "Enviar solicitação"}
        </button>
      </div>
    </div>
  );
}
